<template>
    <div>
        <form-group label="" name="date">
            <div class="timesheet-date-group input-group flex">
                <button class="bg-blue text-white date-arrow-icon"
                        @click.prevent="onClickLeftArrowButton">
                    <i class="far fa-angle-left"></i>
                </button>
                <input
                    name="date"
                    type="text"
                    readonly
                    :value="formattedDate"
                    ref="dateField"
                    class="input text-center">
                <button class="text-white date-arrow-icon"
                        :class="{
                                'bg-grey': disabledDateRightArrow,
                                'bg-blue': !disabledDateRightArrow
                            }"
                        :disabled="disabledDateRightArrow"
                        @click.prevent="onClickRightArrowButton">
                    <i class="far fa-angle-right"></i>
                </button>
            </div>
        </form-group>

        <div class="flex flex-row"
             v-for="(interval, index) in intervals"
             :key="`interval-${index}`">
            <div class="flex-1 pr-4">
                <form-group label="From" :name="`intervals.${index}.start`" :required="true">
                    <timepicker v-model="interval.start" :has-icon="false" :time_24hr="true" />
                </form-group>
            </div>

            <div class="flex-1" :class="{'pr-4': intervals.length > 1}">
                <form-group label="To" :name="`intervals.${index}.end`" :required="true">
                    <timepicker v-model="interval.end" :has-icon="false" :time_24hr="true" />
                </form-group>
            </div>

            <div v-if="intervals.length > 1"
                 class="h-full flex w-8 py-4 justify-center items-center">
                <button @click.prevent="removeInterval(index)"
                        class="btn btn--sm btn--outline__red mt-2 py-2 px-3 rounded">
                    <i class="fal fa-times"></i>
                </button>
            </div>
        </div>

        <div class="help-text text-red pb-8"
             v-if="hasError('intervals')"
             v-html="getError('intervals')"
        ></div>

        <div class="flex flex-row pb-8">
            <div class="flex-1 pt-2 pr-4 text-left">
                <label>
                    <toggle-button class="mr-2 uppercase"
                                   v-model="value.rest_day"
                                   :labels="{checked: 'Yes', unchecked: 'No'}"
                                   :color="{checked: '#010030', unchecked: '#c0c0c0'}"
                                   :sync="true"></toggle-button>
                    <span class="text-blue font-bold uppercase">Rest Day</span>
                </label>
            </div>
            <div class="flex w-8 text-right">
                <button @click.prevent="addInterval"
                        class="btn btn--sm btn--outline__green px-3 rounded">
                    <i class="fal fa-plus"></i>
                </button>
            </div>
        </div>

        <div v-if="showInfo" class="inline-flex h-full w-full p-2 text-blue mb-8 timesheet-bg-info">
            <span class="text-3xl px-2 py-4">
                <i class="fal fa-info-circle"></i>
            </span>
            <span class="font-heading py-4">
                Hours logged here will only appear on this day even though they cross over into the next day.
            </span>
        </div>

        <h3 v-if="getTotalDuration(intervals)!== false" class="text-center mb-8">
            Duration: {{ getTotalDuration(intervals) }}
        </h3>
    </div>
</template>

<script>
    import moment from 'moment';
    import InteractsWithDates from '@/mixins/InteractsWithDates';
    import NeedsTimesheetsDuration from '@/mixins/NeedsTimesheetsDuration';

    export default {
        mixins: [InteractsWithDates, NeedsTimesheetsDuration],

        props: ['value'],

        data() {
            return {
                intervals: [{start: null, end: null}]
            }
        },

        created() {
            if (!this.value || moment().diff(moment(this.value.date, 'YYYY-MM-DD'), 'days') < 0) {
                this.$router.replace('/timesheets/' + moment().format('YYYY-MM-DD') + '/add');
            }
        },

        mounted() {
            if (this.value && Array.isArray(this.value.intervals)) {
                this.intervals = this.value.intervals;
            }
        },

        methods: {
            save() {
                return {
                    date: this.value.date,
                    intervals: this.intervals,
                    rest_day: this.value.rest_day
                }
            },

            onClickLeftArrowButton() {
                const date = moment(this.value.date, 'YYYY-MM-DD').subtract(1, 'days').format('YYYY-MM-DD');

                this.$router.replace('/timesheets/' + date + '/add');
            },

            onClickRightArrowButton() {
                if (this.value.date === moment().format('YYYY-MM-DD')) {
                    return;
                }

                const date = moment(this.value.date, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD');

                this.$router.replace('/timesheets/' + date + '/add');
            },

            addInterval() {
                let intervals = this.intervals;
                intervals.push({start: null, end: null});
                this.value.intervals = intervals;
            },

            removeInterval(index) {
                this.value.intervals.splice(index, 1);
            },

            hasError(name) {
                return this.$store.getters.hasError(name)
            },
            getError(name) {
                return this.$store.getters.getError(name)
            }
        },

        computed: {
            disabledDateRightArrow() {
                return moment().diff(moment(this.value.date, 'YYYY-MM-DD'), 'days') <= 0;
            },

            formattedDate() {
                if (!this.value.date) {
                    return 'Not specified';
                }

                return moment(this.value.date, 'YYYY-MM-DD').format('DD MMM Y');
            },

            showInfo() {
                return this.intervals.filter(item => {
                    if (!item.start || !item.end) {
                        return false;
                    }

                    const startMinutes = (parseInt(item.start.split(':')[0]) * 60) + parseInt(item.start.split(':')[1])
                    const endMinutes = (parseInt(item.end.split(':')[0]) * 60) + parseInt(item.end.split(':')[1])
                    return startMinutes > endMinutes;
                }).length > 0;
            }
        },

        watch: {
            'value.intervals': {
                deep: true,
                handler(val) {
                    this.intervals = val || [{start: null, end: null}];
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .timesheet-bg-info {
        background-color: #d9edf7;
    }

    .timesheet-date-group {
        .date-arrow-icon {
            font-size: 1rem;
            padding: 0 0.75rem;
        }

        .input:focus {
            outline:none;
            outline-width: 0;
            box-shadow: none;
            -moz-box-shadow: none;
            -webkit-box-shadow: none;
        }
    }
</style>
